import { Dialog, Transition } from '@headlessui/react';
import { ArrowPathIcon } from '@heroicons/react/20/solid';
import { Fragment, useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import useActivationOrgReassignment from './hooks/useActivationOrgReassignment';
import useOrgs from '../../Orgs/components/hooks/useOrgs';

interface ReassignOrgModalProps {
    activationId: number;
    activationName: string;
    currentOrgId: number;
    onDismiss: () => void;
    onSuccess?: (
        newOrgId: number,
        newOrgName: string,
        oldOrgName: string,
    ) => void;
}

export default function ReassignOrgModal({
    activationId,
    activationName,
    currentOrgId,
    onDismiss,
    onSuccess,
}: ReassignOrgModalProps) {
    const cancelButtonRef = useRef(null);
    const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
    const [orgs, setOrgs] = useState<any[]>([]);
    const reassignButton = useRef<HTMLButtonElement | null>(null);

    const { reassignActivationOrg, isLoading } = useActivationOrgReassignment();
    const { listOrgs } = useOrgs();

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const orgList = await listOrgs();
                setOrgs(orgList);
            } catch (error) {
                console.error('Failed to fetch organizations:', error);
                toast.error('Failed to load organizations');
            }
        };
        fetchOrgs();
    }, [listOrgs]);

    const handleReassign = async () => {
        if (!selectedOrgId) {
            toast.error('Please select an organization');
            return;
        }

        if (selectedOrgId === currentOrgId) {
            toast.error('Please select a different organization');
            return;
        }

        if (reassignButton.current) {
            reassignButton.current.disabled = true;
        }

        try {
            const result = await reassignActivationOrg(
                activationId,
                selectedOrgId,
            );

            if (result?.success) {
                const newOrgName =
                    orgs.find((org) => org.id === selectedOrgId)?.name ||
                    'Unknown';
                const oldOrgName = currentOrgName;

                onSuccess?.(selectedOrgId, newOrgName, oldOrgName);
                onDismiss();
            } else {
                toast.error(
                    result?.message ||
                        'Failed to reassign activation organization',
                );
                if (reassignButton.current) {
                    reassignButton.current.disabled = false;
                }
            }
        } catch (error) {
            toast.error('An unexpected error occurred');
            if (reassignButton.current) {
                reassignButton.current.disabled = false;
            }
        }
    };

    const currentOrgName =
        orgs.find((org) => org.id === currentOrgId)?.name || 'Unknown';
    const availableOrgs = orgs.filter((org) => org.id !== currentOrgId);

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-10"
                initialFocus={cancelButtonRef}
                onClose={() => onDismiss()}
                open={true}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ArrowPathIcon
                                                className="h-6 w-6 text-orange-600"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-base font-semibold leading-6 text-gray-900"
                                            >
                                                Reassign Organization
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Reassign "{activationName}"
                                                    from "{currentOrgName}" to a
                                                    different organization.
                                                </p>
                                                <p className="text-sm text-red-600 mt-2">
                                                    <strong>Warning:</strong>{' '}
                                                    This will move all
                                                    associated data (galleries,
                                                    media, shares) to the new
                                                    organization.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-12 mt-4 p-2">
                                        <div>
                                            <label
                                                htmlFor="organization"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                New Organization
                                            </label>
                                            <div className="mt-2">
                                                <select
                                                    id="organization"
                                                    name="organization"
                                                    value={selectedOrgId}
                                                    onChange={(e) =>
                                                        setSelectedOrgId(
                                                            parseInt(
                                                                e.target.value,
                                                                10,
                                                            ),
                                                        )
                                                    }
                                                    className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-orange-600 sm:text-sm sm:leading-6"
                                                >
                                                    <option value={0}>
                                                        Select an
                                                        organization...
                                                    </option>
                                                    {availableOrgs.map(
                                                        (org) => (
                                                            <option
                                                                key={org.id}
                                                                value={org.id}
                                                            >
                                                                {org.name}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        ref={reassignButton}
                                        disabled={isLoading || !selectedOrgId}
                                        className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                                        onClick={handleReassign}
                                    >
                                        {isLoading
                                            ? 'Reassigning...'
                                            : 'Reassign'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={() => onDismiss()}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
